import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  ReactNode
} from 'react';
import { useFormikContext } from 'formik';
import { paths } from 'routes/paths';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { datadogLogs } from '@datadog/browser-logs';

import applicationNotification from 'screens/Applications&WorkOrders/Applications/Components/Notification';
import { requestPrintWhenReady } from 'components/Notification/genericNotification';
import usePreferences, { PreferenceKey } from 'hooks/usePreferences';
import { Context } from 'components/Store';
import useOperationEvents from 'hooks/useOperationEvents';
import workOrderNotification from 'screens/Applications&WorkOrders/WorkOrders/Components/Notification';
import {
  Flow,
  RoutePathState
} from 'screens/Applications&WorkOrders/RouteAdmin';
import { OperationType as OperationTypeEnum } from 'components/RecordsListView/constants';
import useWeatherApi, { defaultWeatherData } from 'hooks/useWeatherApi';
import useWorkOrders from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrders';
import useInventoryApi from 'hooks/useInventoryApi';
import { parseServerError } from 'helpers/errorHelpers';
import { createDuplicateLocationState } from 'screens/Applications&WorkOrders/Components/ActionMenu/helpers';
import { SEARCH_TEXT_VALUE_FFT } from 'reducers/reducer';
import { FormValues, WeatherData, WeatherMode } from '../Constants/formikData';
import {
  normalizeTemperatureUnit,
  normalizePercentageUnit,
  normalizeWindSpeedUnit,
  convertTemperature,
  convertWindSpeed,
  normalizeSkyCondition
} from '../Components/WeatherStep/helpers';
import useApplication from '../../Applications/Hooks/useApplication';
import LayoutStrings from '../Constants/layoutStrings';
import showFutureWeatherConfirmModal from '../Helpers/futureWeatherConfirmModal';
import showWeatherErrorModal, {
  WEATHER_ERROR_TYPES
} from '../Helpers/weatherErrorModal';

// Exported helper to merge form weather data with API weather data
// Preserves ONLY manually modified form values, fills everything else from API data
export const mergeFormWithApiWeather = (
  formPeriod: WeatherData | undefined,
  apiPeriod: WeatherData | undefined
): WeatherData | undefined => {
  if (!apiPeriod) return formPeriod;
  if (!formPeriod) return apiPeriod;

  // Start with API data as base (fresh data)
  const merged: WeatherData = { ...apiPeriod };

  // Only overlay MANUALLY MODIFIED fields from form (not all non-empty values)
  const manuallyModifiedFields = formPeriod.manuallyModifiedFields || [];

  manuallyModifiedFields.forEach(field => {
    const formValue = formPeriod[field as keyof WeatherData];
    // Always apply manually modified fields, even if cleared (empty string)
    // This allows users to intentionally clear a field value
    ((merged as unknown) as Record<string, unknown>)[field] = formValue;

    // Also preserve associated units for this field (only if the value is not empty)
    if (formValue !== '' && formValue !== null && formValue !== undefined) {
      const unitFieldMap: Record<string, string[]> = {
        temperature: ['temperatureSelectedUnit', 'temperatureUnit'],
        windSpeed: ['windSpeedSelectedUnit', 'windSpeedUnit'],
        humidity: ['humidityUnit']
      };
      const unitFields = unitFieldMap[field];
      if (unitFields) {
        unitFields.forEach(unitField => {
          const unitValue = formPeriod[unitField as keyof WeatherData];
          if (unitValue) {
            ((merged as unknown) as Record<string, unknown>)[
              unitField
            ] = unitValue;
          }
        });
      }
    }
  });

  // Preserve manuallyModifiedFields array
  if (manuallyModifiedFields.length > 0) {
    merged.manuallyModifiedFields = manuallyModifiedFields;
  }

  return merged;
};

export enum OperationType {
  APPLICATION = 'application',
  WORK_ORDER = 'workOrder'
}

export interface AgOperationUtilityState {
  selectedCropSeasons: any[];
  // Weather step utilities - simple boolean toggle
  autoWeatherEnabled: boolean;
  hasModifiedWeatherData: boolean;
  // Products step utilities - flexible structure for future development
  // NOSONAR TODO: Product utilities structure will be defined based on actual requirements
  hasTankInfoPreferencesBeenLoaded?: boolean;
  loadedUserPreferences?: Record<string, any>;
  showTankInfoBanner?: boolean;
  products: Record<string, any>;
  isEditMode?: boolean;
  selectedCarrier?: string;

  originalCarrierIsHidden?: boolean; // Track original carrier hidden state for edit/duplicate mode

  // Original dates from loaded application (for edit mode comparison)
  // Stored as timestamps (milliseconds) for simple comparison
  originalStartDateTime?: number;
  originalEndDateTime?: number;

  // Crop zone ids from the record as originally loaded (edit mode), kept
  // separate from live Formik values so they survive PropertiesStep
  // unmounting on step navigation (used to keep archived crop zones
  // visible in SelectPropertiesDrawerV2 after deselect + save)
  originalCropZoneIds?: string[];

  // Path to redirect to on cancel (e.g., when creating app from work order)
  returnPath?: string;
}

interface AgOperationUtilityContextType {
  utilityState: AgOperationUtilityState;
  updateUtilityState: (updates: Partial<AgOperationUtilityState>) => void;

  // Weather utilities - simple helpers
  setAutoWeatherEnabled: (enabled: boolean) => void;
  setHasModifiedWeatherData: (modified: boolean) => void;
  fetchWeatherData: (preservedWeatherData?: {
    start?: WeatherData;
    end?: WeatherData;
  }) => Promise<void>;
  resetOriginalWeatherData: () => void;
  isWeatherLoading: boolean;
  weatherError: string | null;
  apiErrorValue: string | null;

  // Original weather data from API (source of truth for comparisons)
  originalWeatherData: {
    start?: WeatherData;
    end?: WeatherData;
  } | null;

  // Save utilities - handles auto-weather fetching before save
  handleSaveApplication: (stepName?: string) => Promise<void>;
  handleSaveWorkOrder: (stepName?: string) => Promise<void>;
  isSavingForm: boolean;

  // Products utilities - flexible helpers for future development
  updateProductUtilities: (updates: Record<string, any>) => void;
  operationType?: OperationType;
  setOperationType: (type: OperationType) => void;

  // Helper to check if application dates are in the future (weather not available)
  areDatesInFuture: () => boolean;

  // Track if user has visited the weather tab in edit mode
  hasVisitedWeatherTab: boolean;
  setHasVisitedWeatherTab: (visited: boolean) => void;

  // Set original dates when loading application in edit mode
  // Accepts moment objects and stores as timestamps for comparison
  setOriginalDates: (
    startDateTime?: { valueOf(): number },
    endDateTime?: { valueOf(): number }
  ) => void;

  // Set original crop zone ids when loading application/work order in edit mode
  setOriginalCropZones: (cropZones?: Array<{ id: string }>) => void;

  // Propagate applicationMethod to all products (call from Details step dropdown onChange)
  propagateApplicationMethodToProducts: (applicationMethod: string) => void;
}

export const defaultUtilityState: AgOperationUtilityState = {
  selectedCropSeasons: JSON.parse(
    localStorage.getItem('selectedCropSeasons') ?? '[]'
  ),
  autoWeatherEnabled: true, // Default to true (ON by default)
  hasModifiedWeatherData: false,
  hasTankInfoPreferencesBeenLoaded: false,
  showTankInfoBanner: false,
  products: {},
  selectedCarrier: undefined,
  isEditMode: undefined,
  originalStartDateTime: undefined,
  originalEndDateTime: undefined,
  originalCropZoneIds: undefined
};

const AgOperationUtilityContext = createContext<
  AgOperationUtilityContextType | undefined
>(undefined);

export const GO_BACK = 'GO_BACK';

/**
 * AgOperationUtilityProvider - Manages utility state across all Agricultural Operation flows
 *
 * This context provides persistent state management for utility features across:
 * - Create Application flow (weather auto-fetch, product calculations, etc.)
 * - Edit Application flow (future)
 * - Work Orders flow (future)
 * - Any other agricultural operation features
 *
 * Usage:
 * <AgOperationUtilityProvider>
 *   <AgOperationFlowContent />
 * </AgOperationUtilityProvider>
 *
 * The provider is automatically included in RouteAdmin, so all agricultural operation
 * flows have access to these utilities without additional setup.
 */
export const AgOperationUtilityProvider: React.FC<{
  children: ReactNode;
  returnPath?: string;
}> = ({ children, returnPath }) => {
  const applicationEvents = useOperationEvents(OperationTypeEnum.APPLICATION);
  const workOrderEvents = useOperationEvents(OperationTypeEnum.WORK_ORDER);
  const [, dispatch] = (useContext(Context) as unknown) as [
    Record<string, any>,
    React.Dispatch<any>
  ];
  const [utilityState, setUtilityState] = useState<AgOperationUtilityState>({
    ...defaultUtilityState,
    returnPath
  });

  const [operationType, setOperationType] = useState<OperationType>();

  // Store the original weather data from API as source of truth
  const [originalWeatherData, setOriginalWeatherData] = useState<{
    start?: WeatherData;
    end?: WeatherData;
  } | null>(null);

  const [isSavingForm, setIsSavingForm] = useState(false);

  // Track if user has visited the weather tab in edit mode
  // Used to determine if we should show the "Automatic weather added" modal
  const [hasVisitedWeatherTab, setHasVisitedWeatherTab] = useState(false);

  // Use ref to track weather fetch completion for save handler
  const weatherFetchResolverRef = useRef<(() => void) | null>(null);
  const weatherFetchRejecterRef = useRef<((reason?: any) => void) | null>(null);

  // Use ref to store the latest fetched weather data for injection at save time
  const latestWeatherDataRef = useRef<{
    start?: WeatherData;
    end?: WeatherData;
  } | null>(null);

  // Use ref to store weather data that should be preserved during fetch (for merge)
  // This stores the current form values before a fetch, so we can merge modified fields
  const preservedWeatherDataRef = useRef<{
    start?: WeatherData;
    end?: WeatherData;
  } | null>(null);

  const [
    {
      organization: { id: orgId }
    }
  ] = (useContext(Context) as unknown) as [
    Record<string, any>,
    React.Dispatch<any>
  ];

  // Get Formik context to access form values and setFieldValue
  const formikContext = useFormikContext<FormValues>();
  const { values, setFieldValue } = formikContext || {};

  const isWeatherToggleOn = values?.weather?.mode === WeatherMode.AUTOMATIC;

  const { upsertPreference, findPreferenceByKey } = usePreferences();

  // Use weather API hook
  const {
    weatherData,
    loading,
    error: weatherError,
    apiErrorValue,
    clearApiError,
    fetchWeatherForDate
  } = useWeatherApi();

  const history = useHistory();
  const routeState = history?.location?.state as RoutePathState;

  const isDuplicateApplicationFlow = routeState?.flow === Flow.DUPLICATE;

  const inventoryAPI = useInventoryApi('inventory/products-prices');
  const { createApplication, editApplication } = useApplication();
  const { createWorkOrder, editWorkOrder } = useWorkOrders();

  const updateUtilityState = useCallback(
    (updates: Partial<AgOperationUtilityState>) => {
      setUtilityState(prev => ({ ...prev, ...updates }));
    },
    []
  );

  const setAutoWeatherEnabled = useCallback(
    (enabled: boolean) => {
      updateUtilityState({ autoWeatherEnabled: enabled });
    },
    [updateUtilityState]
  );

  const setHasModifiedWeatherData = useCallback(
    (modified: boolean) => {
      updateUtilityState({ hasModifiedWeatherData: modified });
    },
    [updateUtilityState]
  );

  // Set original dates when loading application in edit mode
  // Converts moment objects to timestamps for simple comparison
  const setOriginalDates = useCallback(
    (
      startDateTime?: { valueOf(): number },
      endDateTime?: { valueOf(): number }
    ) => {
      updateUtilityState({
        originalStartDateTime: startDateTime?.valueOf(),
        originalEndDateTime: endDateTime?.valueOf()
      });
    },
    [updateUtilityState]
  );

  // Set original crop zone ids when loading application/work order in edit mode
  const setOriginalCropZones = useCallback(
    (cropZones?: Array<{ id: string }>) => {
      updateUtilityState({
        originalCropZoneIds: (cropZones ?? []).map(cropZone => cropZone.id)
      });
    },
    [updateUtilityState]
  );

  // Helper to preserve only manually modified fields on top of default data
  // Used both for tab switching and for saving with future dates
  const preserveOnlyManualFields = useCallback(
    (currentData: WeatherData | undefined): WeatherData => {
      const modifiedFields = currentData?.manuallyModifiedFields || [];
      if (modifiedFields.length === 0) {
        return { ...defaultWeatherData };
      }

      // Start with default data, then overlay ONLY the manually modified fields
      const preserved: WeatherData = { ...defaultWeatherData };

      modifiedFields.forEach((field: string) => {
        if (currentData && field in currentData) {
          ((preserved as unknown) as Record<string, unknown>)[field] =
            currentData[field as keyof WeatherData];

          // Also preserve the associated unit for this field if it exists
          const unitFieldMap: Record<string, string> = {
            temperature: 'temperatureSelectedUnit',
            windSpeed: 'windSpeedSelectedUnit',
            humidity: 'humidityUnit'
          };
          const unitField = unitFieldMap[field];
          if (unitField && currentData[unitField as keyof WeatherData]) {
            ((preserved as unknown) as Record<string, unknown>)[unitField] =
              currentData[unitField as keyof WeatherData];
          }
        }
      });

      // Keep the manuallyModifiedFields array
      preserved.manuallyModifiedFields = modifiedFields;

      return preserved;
    },
    []
  );

  useEffect(() => {
    const loadWeatherPreferences = async (): Promise<void> => {
      // Only load preferences for new applications (not edit mode)
      if (utilityState.isEditMode !== undefined && !utilityState.isEditMode) {
        try {
          const weatherPreference = await findPreferenceByKey(
            PreferenceKey.OPERATION_WEATHER,
            { orgId }
          );

          if (weatherPreference?.preferenceValue) {
            const {
              getWeatherAutomatically
            } = weatherPreference.preferenceValue;
            setFieldValue(
              'weather.mode',
              getWeatherAutomatically
                ? WeatherMode.AUTOMATIC
                : WeatherMode.MANUAL,
              false
            );
            setAutoWeatherEnabled(getWeatherAutomatically);
          }
        } catch (_error) {
          // Silently fail if preferences can't be loaded
          // Application will use default value (true)
          datadogLogs.logger.warn('Failed to load weather preferences:', {
            orgId,
            error: _error
          });
          parseServerError(dispatch)(_error);
        }
      }
    };

    loadWeatherPreferences();
  }, [
    utilityState.isEditMode,
    orgId,
    dispatch,
    findPreferenceByKey,
    setFieldValue,
    setAutoWeatherEnabled
  ]);

  // Track if we've already synced autoWeatherEnabled with the saved mode
  const hasSyncedAutoWeatherRef = useRef(false);

  // Sync autoWeatherEnabled with the saved weather.mode in edit mode (only once on initial load)
  // This ensures the toggle state matches what was saved in the API
  useEffect(() => {
    if (
      utilityState.isEditMode &&
      values?.weather?.mode &&
      !hasSyncedAutoWeatherRef.current
    ) {
      hasSyncedAutoWeatherRef.current = true;
      const shouldBeEnabled = values.weather.mode === WeatherMode.AUTOMATIC;
      if (isWeatherToggleOn !== shouldBeEnabled) {
        setAutoWeatherEnabled(shouldBeEnabled);
      }
    }
  }, [
    utilityState.isEditMode,
    values?.weather?.mode,
    isWeatherToggleOn,
    setAutoWeatherEnabled
  ]);

  // Reset original weather data to null (used when toggle is turned off)
  const resetOriginalWeatherData = useCallback(() => {
    setOriginalWeatherData(null);
  }, []);

  // Centralized weather fetching function
  // preservedWeatherData: optional weather data to preserve (merge modified fields after fetch)
  const fetchWeatherData = useCallback(
    (preservedWeatherData?: {
      start?: WeatherData;
      end?: WeatherData;
    }): Promise<void> => {
      // Store the preserved weather data in ref so useEffect can access it
      preservedWeatherDataRef.current = preservedWeatherData || null;

      if (!values) {
        return Promise.resolve();
      }

      const application = values;
      const cropZones = application?.cropZones ?? [];
      const startDate = application?.startDateTime;
      const endDate = application?.endDateTime;

      if (cropZones.length === 0) {
        return Promise.resolve();
      }

      // Don't fetch weather for future dates - API doesn't support it
      // Instead, set default values for weather and set mode to AUTOMATIC
      const nowUtc = Date.now();
      const isStartDateInFuture = startDate && startDate.valueOf() > nowUtc;
      const isEndDateInFuture = endDate && endDate.valueOf() > nowUtc;
      if (isStartDateInFuture || isEndDateInFuture) {
        // Set original weather data reference to default values for comparison
        // Use spread to create separate copies for start and end to avoid shared reference
        setOriginalWeatherData({
          start: { ...defaultWeatherData },
          end: { ...defaultWeatherData }
        });

        // Build weather data for future dates:
        // - Reset all fields to default
        // - Preserve ONLY the manually modified fields (not API-fetched values)
        const futureWeatherStart = preserveOnlyManualFields(
          values.weather?.start
        );
        const futureWeatherEnd = preserveOnlyManualFields(values.weather?.end);

        setFieldValue(
          'weather',
          {
            mode: WeatherMode.AUTOMATIC,
            start: futureWeatherStart,
            end: futureWeatherEnd
          },
          false
        );

        // Store in ref so determineWeatherToSave uses it
        // instead of the stale values.weather (setFieldValue is async)
        latestWeatherDataRef.current = {
          start: futureWeatherStart,
          end: futureWeatherEnd
        };
        return Promise.resolve();
      }

      // Reset modification state when fetching fresh data
      setHasModifiedWeatherData(false);
      // Single API call fetches weather for both start and end times
      if (cropZones.length > 0 && startDate && endDate) {
        // Return a promise that resolves when weather data is populated in Formik
        const weatherPromise = new Promise<void>((resolve, reject) => {
          weatherFetchResolverRef.current = resolve;
          weatherFetchRejecterRef.current = reject;
          fetchWeatherForDate(cropZones, startDate, endDate);
        });

        weatherPromise.catch(() => {
          // Error is already handled by UI via useWeatherApi hook
          // This catch just prevents "Unhandled Promise Rejection" errors
        });

        return weatherPromise;
      }

      return Promise.resolve();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      values,
      setHasModifiedWeatherData,
      fetchWeatherForDate,
      utilityState.isEditMode
    ]
  );

  // Helper function to convert parsed weather data to WeatherData format
  const convertToWeatherData = (data: Partial<WeatherData>): WeatherData => {
    // Normalize units from API
    const apiTemperatureUnit = normalizeTemperatureUnit(
      data?.temperatureUnit ?? ''
    );
    const userPreferredTemperatureUnit = '°F'; // Always prefer Fahrenheit
    const userPreferredWindSpeedUnit = 'MPH'; // Always prefer MPH (uppercase to match dropdown)

    // Convert temperature value to user's preferred unit if needed
    let convertedTemperature = data?.temperature ?? '';
    if (
      convertedTemperature &&
      apiTemperatureUnit &&
      userPreferredTemperatureUnit
    ) {
      const tempValue = Number.parseFloat(convertedTemperature);
      if (
        !Number.isNaN(tempValue) &&
        apiTemperatureUnit !== userPreferredTemperatureUnit
      ) {
        const convertedValue = convertTemperature(
          tempValue,
          apiTemperatureUnit,
          userPreferredTemperatureUnit
        );
        convertedTemperature = convertedValue.toFixed(1); // Round to 1 decimal place
      }
    }

    // Convert wind speed value to user's preferred unit if needed
    const apiWindSpeedUnit = normalizeWindSpeedUnit(data?.windSpeedUnit ?? '');
    let convertedWindSpeed = data?.windSpeed ?? '';
    if (convertedWindSpeed && apiWindSpeedUnit && userPreferredWindSpeedUnit) {
      const windValue = Number.parseFloat(convertedWindSpeed);
      if (
        !Number.isNaN(windValue) &&
        apiWindSpeedUnit !== userPreferredWindSpeedUnit
      ) {
        const convertedValue = convertWindSpeed(
          windValue,
          apiWindSpeedUnit,
          userPreferredWindSpeedUnit
        );
        convertedWindSpeed = convertedValue.toFixed(1); // Round to 1 decimal place
      }
    }

    return {
      skyCondition: normalizeSkyCondition(data?.skyCondition ?? ''),
      temperature: convertedTemperature,
      humidity: data?.humidity?.toString() ?? '',
      windSpeed: convertedWindSpeed,
      windDirection: data?.windDirection ?? '',
      // Backend already returns soilMoisture as a category string (Dry/Moist/Wet/Unknown)
      // No conversion needed - use it directly
      soilMoisture: data?.soilMoisture ?? '',
      // Units from API (keep original backend format for windSpeedUnit)
      temperatureUnit: apiTemperatureUnit,
      humidityUnit: normalizePercentageUnit(data?.humidityUnit ?? ''),
      windSpeedUnit: data?.windSpeedUnit ?? '',
      // User selected units (always use preferred units since values are converted)
      temperatureSelectedUnit: userPreferredTemperatureUnit,
      windSpeedSelectedUnit: userPreferredWindSpeedUnit,
      manuallyModifiedFields: data?.manuallyModifiedFields || []
    };
  };

  // useEffect to populate form data when weatherData changes
  useEffect(() => {
    if (!weatherData.startDate && !weatherData.endDate) {
      return;
    }

    // Weather data is already structured from the new API
    const startWeather = weatherData.startDate ?? {};
    const endWeather = weatherData.endDate ?? {};
    const weatherValues = {
      start: convertToWeatherData(startWeather),
      end: convertToWeatherData(endWeather)
    };

    // Populate form with weather data
    // Note: We don't check autoWeatherEnabled here because if weatherData exists,
    // it means a fetch was initiated when the toggle was ON. The toggle state
    // controls whether to initiate the fetch, not whether to use the results.

    // Helper function to merge API weather data with preserved form data
    // This preserves user's modified fields while updating non-modified fields with fresh API data
    const mergeWeatherData = (
      apiData: WeatherData | undefined,
      preservedData: WeatherData | undefined
    ): WeatherData | undefined => {
      if (!apiData) return preservedData;
      if (!preservedData) return apiData;

      const modifiedFields = preservedData.manuallyModifiedFields || [];
      if (modifiedFields.length === 0) {
        // No manual modifications, use API data entirely
        return apiData;
      }

      // Start with API data, then overlay user's modified fields
      const mergedData: WeatherData = { ...apiData };

      // For each manually modified field, preserve the user's value and its associated unit
      // IMPORTANT: This includes preserving cleared/empty values (empty strings or undefined)
      modifiedFields.forEach((field: string) => {
        // Always preserve the user's modified value, even if it's empty/undefined
        // This ensures cleared dropdown values stay cleared instead of being repopulated
        ((mergedData as unknown) as Record<string, unknown>)[field] =
          preservedData[field as keyof WeatherData];

        // Also preserve the associated unit for this field if it exists
        const unitFieldMap: Record<string, string> = {
          temperature: 'temperatureSelectedUnit',
          windSpeed: 'windSpeedSelectedUnit',
          humidity: 'humidityUnit'
        };
        const unitField = unitFieldMap[field];
        if (unitField) {
          ((mergedData as unknown) as Record<string, unknown>)[unitField] =
            preservedData[unitField as keyof WeatherData];
        }
      });

      // Preserve the manuallyModifiedFields array
      mergedData.manuallyModifiedFields = modifiedFields;

      return mergedData;
    };

    // Check if we have preserved weather data to merge (passed from WeatherStep)
    const preservedData = preservedWeatherDataRef.current;
    const hasPreservedModifiedStart =
      (preservedData?.start?.manuallyModifiedFields?.length ?? 0) > 0;
    const hasPreservedModifiedEnd =
      (preservedData?.end?.manuallyModifiedFields?.length ?? 0) > 0;
    const hasPreservedModifications =
      hasPreservedModifiedStart || hasPreservedModifiedEnd;

    // Determine final weather values:
    // - If we have preserved data with modifications, merge it
    // - Otherwise, use API data directly
    const finalWeatherValues = hasPreservedModifications
      ? {
          start: mergeWeatherData(weatherValues.start, preservedData?.start),
          end: mergeWeatherData(weatherValues.end, preservedData?.end)
        }
      : weatherValues;

    // Store the final (merged) weather data in ref for save handler to use
    latestWeatherDataRef.current = finalWeatherValues;

    // Clear the preserved data ref after using it
    preservedWeatherDataRef.current = null;

    // Always set form values with the final (possibly merged) weather data
    setFieldValue(
      'weather',
      {
        mode: WeatherMode.AUTOMATIC,
        ...finalWeatherValues
      },
      false
    );

    // Also store the original weather data as source of truth for comparisons
    const originalData: {
      start?: WeatherData;
      end?: WeatherData;
    } = {};

    // Store start weather data if it exists
    if (startWeather && Object.values(startWeather).some(Boolean)) {
      originalData.start = convertToWeatherData(startWeather);
    }

    // Store end weather data if it exists
    if (endWeather && Object.values(endWeather).some(Boolean)) {
      originalData.end = convertToWeatherData(endWeather);
    }

    setOriginalWeatherData(
      Object.keys(originalData).length > 0 ? originalData : null
    );

    // A failed request also resets weather data to defaults. Do not mistake
    // those defaults for a successful fetch while the save handler is waiting.
    if (weatherFetchResolverRef.current && !weatherError && !apiErrorValue) {
      weatherFetchResolverRef.current();
      weatherFetchResolverRef.current = null;
      weatherFetchRejecterRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherData, setFieldValue, utilityState.isEditMode]);

  // Handle weather API errors - reject the promise if save handler is waiting
  useEffect(() => {
    if (weatherError) {
      // If there's a rejecter waiting (save flow), reject the promise
      if (weatherFetchRejecterRef.current) {
        weatherFetchRejecterRef.current(new Error(weatherError));
        weatherFetchResolverRef.current = null;
        weatherFetchRejecterRef.current = null;
      }
      // If no rejecter (manual fetch from weather tab), the error is already
      // displayed in the UI via the useWeatherApi hook, so we don't need to do anything
      // The error will be cleared when user refreshes or the component unmounts
    }
  }, [weatherError, weatherData]);

  // Handle API errors (e.g., centroid calculation) - reject the promise if save handler is waiting
  useEffect(() => {
    if (apiErrorValue && weatherFetchRejecterRef.current) {
      weatherFetchRejecterRef.current(new Error(apiErrorValue));
      weatherFetchRejecterRef.current = null;
      weatherFetchResolverRef.current = null;
    }
  }, [apiErrorValue, weatherData]);

  // Propagate applicationMethod to all products
  // Called explicitly from Details step dropdown onChange handler
  const propagateApplicationMethodToProducts = useCallback(
    (applicationMethod: string) => {
      if (!values?.products?.length || !applicationMethod) return;

      const needsUpdate = values.products.some(
        product => product.applicationMethod !== applicationMethod
      );

      if (needsUpdate) {
        const updatedProducts = values.products.map(product => ({
          ...product,
          applicationMethod
        }));
        setFieldValue('products', updatedProducts);
      }
    },
    [values?.products, setFieldValue]
  );

  // Save tank information preferences
  const saveTankInfoPreferences = useCallback(async (): Promise<void> => {
    if (
      values.isTankInfoEnabled &&
      (values.tankSize === undefined || values.overallRatePerAcre === undefined)
    )
      return;

    if (!utilityState.isEditMode) {
      let preferenceValue;
      try {
        // When toggle is ON, save all current values
        if (values.isTankInfoEnabled) {
          const carrier = values.products?.find(p => p.isCarrier);
          preferenceValue = {
            toggleEnabled: true,
            tankSize: values.tankSize ?? 0,
            overallRatePerAcre: values.overallRatePerAcre ?? 0,
            carrierId: carrier?.id,
            isCarrierHidden: carrier?.isHidden ?? false
          };
        } else {
          // When toggle is OFF, preserve existing tank values
          const existingPreference = await findPreferenceByKey(
            PreferenceKey.OPERATION_TANK_INFORMATION,
            { orgId }
          );

          // If no existing preference exists (user has never set tank info),
          // there's nothing to persist — skip saving
          if (!existingPreference?.preferenceValue) {
            return;
          }

          preferenceValue = {
            ...existingPreference.preferenceValue,
            toggleEnabled: false
          };
        }

        await upsertPreference({
          preferenceKey: PreferenceKey.OPERATION_TANK_INFORMATION,
          preferenceValue,
          orgId
        });
      } catch (_error) {
        // Silently fail if preferences can't be saved
        // eslint-disable-next-line no-console
        datadogLogs.logger.warn(
          'Failed to save tank information preferences:',
          { ...preferenceValue, orgId, id: values.id },
          _error as Error
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, utilityState.isEditMode, orgId]);

  // Save weather preferences (separate from tank preferences)
  const saveWeatherPreferences = useCallback(async (): Promise<void> => {
    if (!utilityState.isEditMode) {
      try {
        // Backend stores weather preferences separately in OPERATION_WEATHER
        const preferenceValue = {
          getWeatherAutomatically: Boolean(isWeatherToggleOn)
        };

        await upsertPreference({
          preferenceKey: PreferenceKey.OPERATION_WEATHER,
          preferenceValue,
          orgId
        });
      } catch (_error) {
        // Silently fail if preferences can't be saved
        datadogLogs.logger.error('Failed to save weather preferences:', {
          autoWeatherEnabled: isWeatherToggleOn,
          orgId,
          id: values?.id,
          error: _error
        });
        parseServerError(dispatch)(_error);
      }
    }
  }, [
    utilityState.isEditMode,
    isWeatherToggleOn,
    upsertPreference,
    orgId,
    dispatch,
    values?.id
  ]);

  // Helper function to clean weather data - only remove units if their values are empty
  const cleanWeatherPeriod = (period?: WeatherData) => {
    if (!period) return undefined;

    const cleaned: any = { ...period };

    // Only remove temperature units if temperature value is empty/null
    if (
      cleaned.temperature === undefined ||
      cleaned.temperature === null ||
      cleaned.temperature === ''
    ) {
      delete cleaned.temperatureUnit;
      delete cleaned.temperatureSelectedUnit;
    }

    // Only remove wind speed units if windSpeed value is empty/null
    if (
      cleaned.windSpeed === undefined ||
      cleaned.windSpeed === null ||
      cleaned.windSpeed === ''
    ) {
      delete cleaned.windSpeedUnit;
      delete cleaned.windSpeedSelectedUnit;
    }

    // Only remove humidityUnit if humidity value is empty/null
    if (
      cleaned.humidity === undefined ||
      cleaned.humidity === null ||
      cleaned.humidity === ''
    ) {
      delete cleaned.humidityUnit;
    }

    // Only remove soilMoistureUnit if soilMoisture value is empty/null
    if (
      cleaned.soilMoisture === undefined ||
      cleaned.soilMoisture === null ||
      cleaned.soilMoisture === ''
    ) {
      delete cleaned.soilMoistureUnit;
    }

    return cleaned;
  };

  const cleanWeatherDataForSave = (weatherInput?: {
    mode: WeatherMode;
    start?: WeatherData;
    end?: WeatherData;
  }) => {
    if (!weatherInput) return undefined;

    return {
      mode: weatherInput.mode,
      start: cleanWeatherPeriod(weatherInput.start),
      end: cleanWeatherPeriod(weatherInput.end)
    };
  };

  // Helper to check if weather period has any actual data (not just empty strings)
  // Only check the actual value fields, not unit fields
  const hasWeatherData = (period?: WeatherData) => {
    if (!period) return false;
    const valueFields = [
      'temperature',
      'humidity',
      'windSpeed',
      'windDirection',
      'skyCondition',
      'soilMoisture'
    ];
    return valueFields.some(field => {
      const value = period[field as keyof WeatherData];
      return value !== '' && value !== null && value !== undefined;
    });
  };

  // Helper to check if weather data ref contains only empty objects (all fields are empty)
  const isWeatherDataEmpty = useCallback(
    (
      weatherDataToCheck: {
        start?: WeatherData;
        end?: WeatherData;
      } | null
    ) => {
      if (!weatherDataToCheck) return true;
      const hasStartData = hasWeatherData(weatherDataToCheck.start);
      const hasEndData = hasWeatherData(weatherDataToCheck.end);
      return !hasStartData && !hasEndData;
    },
    []
  );

  // Helper to determine what weather data to save based on context
  const determineWeatherToSave = (
    areDatesInFuture: boolean,
    hasFormWeatherData: boolean,
    formWeather: FormValues['weather'],
    latestWeatherData: { start?: WeatherData; end?: WeatherData } | null,
    autoWeatherEnabled: boolean
  ) => {
    // Check if user has manually modified any weather fields
    const hasUserModifiedStart =
      (formWeather?.start?.manuallyModifiedFields?.length ?? 0) > 0;
    const hasUserModifiedEnd =
      (formWeather?.end?.manuallyModifiedFields?.length ?? 0) > 0;
    const hasUserManuallyModifiedWeather =
      hasUserModifiedStart || hasUserModifiedEnd;

    // If dates are in the future and auto weather is enabled:
    // - If user manually modified weather fields, preserve ONLY those fields (not API-fetched values)
    // - Otherwise use latestWeatherData (contains default empty values for future dates)
    if (areDatesInFuture && autoWeatherEnabled) {
      // Preserve ONLY user's manually entered data, not API-fetched values
      if (hasUserManuallyModifiedWeather) {
        return {
          mode: WeatherMode.AUTOMATIC,
          start: preserveOnlyManualFields(formWeather?.start),
          end: preserveOnlyManualFields(formWeather?.end)
        };
      }
      // Use latestWeatherData if available (fresh default data for future dates)
      if (latestWeatherData) {
        return {
          mode: WeatherMode.AUTOMATIC,
          ...latestWeatherData
        };
      }
      // Fallback: if no latestWeatherData, return default weather structure
      return {
        mode: WeatherMode.AUTOMATIC,
        start: { ...defaultWeatherData },
        end: { ...defaultWeatherData }
      };
    }

    // If we have both form data and API data, merge them
    // This ensures user-entered values are preserved while empty fields get API values
    if (hasFormWeatherData && latestWeatherData && autoWeatherEnabled) {
      return {
        mode: formWeather?.mode || WeatherMode.AUTOMATIC,
        start: mergeFormWithApiWeather(
          formWeather?.start,
          latestWeatherData.start
        ),
        end: mergeFormWithApiWeather(formWeather?.end, latestWeatherData.end)
      };
    }

    // If user has form weather data but no API data, use form data
    if (hasFormWeatherData) {
      return formWeather;
    }

    // Use auto-fetched/merged weather data if available (applies to both edit and create modes)
    // In edit mode, latestWeatherData contains API data merged with user's manually modified fields
    if (autoWeatherEnabled && latestWeatherData) {
      return {
        mode: WeatherMode.AUTOMATIC,
        ...latestWeatherData
      };
    }

    // Always save mode with default start/end to preserve toggle state
    const modeToSave =
      formWeather?.mode ||
      (autoWeatherEnabled ? WeatherMode.AUTOMATIC : WeatherMode.MANUAL);
    return {
      mode: modeToSave,
      start: { ...defaultWeatherData },
      end: { ...defaultWeatherData }
    };
  };
  const handleListPrice = useCallback(async (): Promise<void> => {
    if (!values.products || values.products.length === 0) {
      return;
    }
    const productsWithSavedListPrice = values.products
      .filter(productItem => productItem.saveAsNewListPrice)
      .map(productItem => ({
        productId: productItem.id,
        averagePricePerUnit: productItem.priceValue,
        averagePricePerUnitUnit: productItem.priceUnit,
        productDensity: productItem.density
      }));
    if (productsWithSavedListPrice.length === 0) {
      return;
    }

    await (inventoryAPI as any)
      .post(productsWithSavedListPrice)
      .promise.catch((error: any) => {
        if (!axios.isCancel(error)) {
          datadogLogs.logger.error('Error saving product list prices', {
            error
          });
          parseServerError(dispatch)(error);
        }
      });
  }, [values, inventoryAPI, dispatch]);

  // Helper to check if a date is in the future (UTC comparison)
  // Works with Date, Moment, or any object with valueOf() method
  const isDateInFuture = useCallback(
    (date: { valueOf(): number } | null | undefined): boolean => {
      return !!date && date.valueOf() > Date.now();
    },
    []
  );

  // Helper to check if weather confirm modal should be shown before save
  const shouldShowWeatherConfirmModal = useCallback(
    (
      stepName: string | undefined,
      areDatesInFuture: boolean,
      datesHaveChanged: boolean
    ): { shouldShow: boolean; isCreateFlow: boolean } => {
      const isNotWeatherTab = stepName !== LayoutStrings.stepNames.weather;
      const shouldShowCreateFlowModal =
        isNotWeatherTab && areDatesInFuture && isWeatherToggleOn;

      const shouldShowEditFlowModal =
        values?.weather?.mode === WeatherMode.AUTOMATIC &&
        utilityState.isEditMode &&
        isNotWeatherTab &&
        !hasVisitedWeatherTab &&
        !areDatesInFuture &&
        isWeatherToggleOn &&
        (datesHaveChanged || values?.weather?.displayAutomaticWeatherModal);

      return {
        shouldShow:
          shouldShowCreateFlowModal || Boolean(shouldShowEditFlowModal),
        isCreateFlow: shouldShowCreateFlowModal
      };
    },
    [
      isWeatherToggleOn,
      values?.weather,
      utilityState.isEditMode,
      hasVisitedWeatherTab
    ]
  );

  // Helper to extract error type from weather fetch error
  const extractWeatherErrorType = (
    weatherFetchError: unknown
  ): typeof WEATHER_ERROR_TYPES[keyof typeof WEATHER_ERROR_TYPES] => {
    let errorMessage: string | undefined;
    if (weatherFetchError instanceof Error) {
      errorMessage = weatherFetchError.message;
    } else if (typeof weatherFetchError === 'string') {
      errorMessage = weatherFetchError;
    }

    const isValidErrorType =
      errorMessage === WEATHER_ERROR_TYPES.CALC_CENTROID_ERROR ||
      errorMessage === WEATHER_ERROR_TYPES.WEATHER_API_ERROR;

    return isValidErrorType
      ? (errorMessage as typeof WEATHER_ERROR_TYPES[keyof typeof WEATHER_ERROR_TYPES])
      : WEATHER_ERROR_TYPES.WEATHER_API_ERROR;
  };

  // Helper to prepare final values with weather data for save
  const prepareFinalValuesForSave = useCallback(
    (areDatesInFuture: boolean) => {
      const hasFormWeatherData =
        values?.weather &&
        (hasWeatherData(values.weather.start) ||
          hasWeatherData(values.weather.end));

      const weatherToSave = determineWeatherToSave(
        areDatesInFuture,
        Boolean(hasFormWeatherData),
        values?.weather,
        latestWeatherDataRef.current,
        isWeatherToggleOn
      );

      const cleanedWeather = cleanWeatherDataForSave(weatherToSave);
      const displayModalValue = !utilityState.isEditMode;

      const weatherWithModalFlag = cleanedWeather
        ? {
            ...cleanedWeather,
            displayAutomaticWeatherModal: displayModalValue
          }
        : undefined;

      return {
        ...values,
        weather: weatherWithModalFlag
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, isWeatherToggleOn, utilityState.isEditMode]
  );

  // Helper to handle weather fetch and error flow
  const handleWeatherFetchIfNeeded = useCallback(
    async (areDatesInFuture: boolean): Promise<void> => {
      // Check if the cached weather data is empty (contains only default/empty values)
      const hasCachedWeatherEmpty = isWeatherDataEmpty(
        latestWeatherDataRef.current
      );

      // Check if cached data only has manually modified fields (rest are empty/defaults)
      // This indicates the data came from a future-date scenario and needs API data for empty fields
      const cachedData = latestWeatherDataRef.current;
      const hasOnlyManualFields =
        cachedData &&
        ((cachedData.start?.manuallyModifiedFields?.length ?? 0) > 0 ||
          (cachedData.end?.manuallyModifiedFields?.length ?? 0) > 0);

      // Need fresh weather if:
      // 1. No cached weather data at all, OR
      // 2. Cached data is empty AND dates are NOW in the present, OR
      // 3. Cached data only has manual fields AND dates are NOW in the present
      //    (this handles the case where user added manual values on future dates,
      //    then changed to present dates - we need API data merged with manual values)
      const needsFreshWeatherFetch =
        !latestWeatherDataRef.current ||
        (hasCachedWeatherEmpty && !areDatesInFuture) ||
        (hasOnlyManualFields && !areDatesInFuture);

      // Fetch weather if toggle is on and we need fresh data
      // Note: We fetch even if user visited weather tab when dates changed from future to present
      // because the cached data from future dates needs API data (either empty or only manual fields)
      if (isWeatherToggleOn && needsFreshWeatherFetch) {
        try {
          await fetchWeatherData({
            start: values?.weather?.start,
            end: values?.weather?.end
          });
        } catch (weatherFetchError) {
          latestWeatherDataRef.current = null;

          setIsSavingForm(false);
          const errorType = extractWeatherErrorType(weatherFetchError);
          const shouldProceedWithoutWeather = await new Promise<boolean>(
            resolve => {
              showWeatherErrorModal({
                errorType,
                onSaveWithoutWeather: () => {
                  clearApiError();
                  resolve(true);
                  setIsSavingForm(true);
                },
                onContinueEditing: () => {
                  clearApiError();
                  resolve(false);
                }
              });
            }
          );

          if (!shouldProceedWithoutWeather) {
            setIsSavingForm(false);
            throw new Error('User cancelled save due to weather fetch error');
          }
        }
      }
    },
    [
      isWeatherDataEmpty,
      values?.weather,
      isWeatherToggleOn,
      fetchWeatherData,
      clearApiError
    ]
  );

  // sendEditAmplitudeEvent and sendSaveEvent are now handled by useOperationEvents

  // Handle save with automatic weather fetching if needed
  const handleSaveApplication = useCallback(
    async (stepName?: string): Promise<void> => {
      // keep track of API duration, payload, and status for Amplitude Event logging
      let createApplicationApiLengthMs = 0;
      let createApplicationPayload: FormValues | null = null;
      let createApplicationCompleted = false;

      const startDate = values?.startDateTime;
      const endDate = values?.endDateTime;
      const areDatesInFuture =
        isDateInFuture(startDate) || isDateInFuture(endDate);

      const datesHaveChanged =
        startDate?.valueOf() !== utilityState.originalStartDateTime ||
        endDate?.valueOf() !== utilityState.originalEndDateTime;

      const { shouldShow, isCreateFlow } = shouldShowWeatherConfirmModal(
        stepName,
        areDatesInFuture,
        datesHaveChanged
      );

      if (shouldShow) {
        const shouldProceedWithSave = await new Promise<boolean>(resolve => {
          showFutureWeatherConfirmModal({
            onSave: () => resolve(true),
            onContinueEditing: () => resolve(false),
            isEditMode: !isCreateFlow
          });
        });

        if (!shouldProceedWithSave) {
          return;
        }
      }

      try {
        setIsSavingForm(true);
        dispatch({
          type: SEARCH_TEXT_VALUE_FFT,
          payload: { valueInput: '' }
        });
        await saveTankInfoPreferences();
        await handleWeatherFetchIfNeeded(areDatesInFuture);

        const finalValues = prepareFinalValuesForSave(areDatesInFuture);

        if (utilityState.isEditMode) {
          const editStartedAt = performance.now();
          try {
            const editedApplication = await editApplication(
              finalValues.id!,
              finalValues
            );
            const editApiLengthMs = Math.round(
              performance.now() - editStartedAt
            );
            applicationEvents.sendSaveEditsEvent(
              'Success',
              editApiLengthMs,
              undefined,
              stepName
            );

            await handleListPrice();
            history.push(paths.applications, {
              application: editedApplication
            });
            applicationNotification('EDIT_SUCCESS');
          } catch (editError) {
            const editApiLengthMs = Math.round(
              performance.now() - editStartedAt
            );
            applicationEvents.sendSaveEditsEvent(
              'Error',
              editApiLengthMs,
              editError,
              stepName
            );

            throw editError;
          }
          return;
        }

        await saveWeatherPreferences();

        createApplicationPayload = finalValues;
        const createApplicationStartedAt = performance.now();

        const createdApplication = await createApplication(finalValues)
          .then(response => {
            createApplicationApiLengthMs = Math.round(
              performance.now() - createApplicationStartedAt
            );
            createApplicationCompleted = true;
            return response;
          })
          .catch(error => {
            createApplicationApiLengthMs = Math.round(
              performance.now() - createApplicationStartedAt
            );
            createApplicationCompleted = true;
            throw error;
          });

        (globalThis as any).__pendingApplicationPrint = {
          application: createdApplication,
          timestamp: Date.now()
        };

        await handleListPrice();

        history.push(paths.applications, {
          createdApplication,
          ...(routeState?.flow === Flow.FROM_WORK_ORDER_TO_APPLICATION && {
            sourceWorkOrderId: routeState.operation?.id
          })
        });

        applicationNotification('CREATE_SUCCESS', undefined, {
          onDuplicate: () => {
            applicationEvents.sendInitiateDuplicateEvent('Confirmation toast');

            history.push(
              `${paths.applications}/create`,
              createDuplicateLocationState(
                createdApplication,
                OperationTypeEnum.APPLICATION
              )
            );
          },
          onPrint: () => {
            // The banner can now appear before the applications list (and its
            // print listener) has mounted, so queue the dispatch until ready.
            requestPrintWhenReady(() => {
              const event = new CustomEvent('printApplication', {
                detail: { application: createdApplication }
              });
              globalThis.dispatchEvent(event);
            });
          }
        });

        // send save application success event to Amplitude with API duration and payload details
        applicationEvents.sendSaveEvent(
          'Success',
          createApplicationApiLengthMs,
          createApplicationPayload,
          isDuplicateApplicationFlow
        );
      } catch (e) {
        // Skip error notification if user cancelled save due to weather fetch error
        // (weather error modal already handled this case)
        if (
          e instanceof Error &&
          e.message === 'User cancelled save due to weather fetch error'
        ) {
          return;
        }
        datadogLogs.logger.error('Error saving application', { error: e });
        applicationNotification('CREATE_OR_EDIT_ERROR');

        if (
          !utilityState.isEditMode &&
          createApplicationCompleted &&
          createApplicationPayload
        ) {
          applicationEvents.sendSaveEvent(
            'Error',
            createApplicationApiLengthMs,
            createApplicationPayload,
            isDuplicateApplicationFlow,
            e
          );
        }
      } finally {
        setIsSavingForm(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isWeatherToggleOn,
      utilityState.isEditMode,
      orgId,
      values,
      handleWeatherFetchIfNeeded,
      saveTankInfoPreferences,
      saveWeatherPreferences,
      createApplication,
      editApplication,
      history,
      shouldShowWeatherConfirmModal,
      prepareFinalValuesForSave,
      handleListPrice,
      applicationEvents
    ]
  );

  const isDuplicateWorkOrderFlow = routeState?.flow === Flow.DUPLICATE;

  const handleSaveWorkOrder = useCallback(
    async (stepName?: string): Promise<void> => {
      let createWorkOrderApiLengthMs = 0;
      let createWorkOrderPayload: FormValues | null = null;
      let createWorkOrderCompleted = false;

      try {
        setIsSavingForm(true);
        dispatch({
          type: SEARCH_TEXT_VALUE_FFT,
          payload: { valueInput: '' }
        });
        // Save preferences whenever tank information values change
        await saveTankInfoPreferences();
        // Note: Weather preferences are only saved for applications, not work orders

        if (utilityState.isEditMode) {
          const editStartedAt = performance.now();
          try {
            const editedWorkOrder = await editWorkOrder(values.id!, values);
            const editApiLengthMs = Math.round(
              performance.now() - editStartedAt
            );
            workOrderEvents.sendSaveEditsEvent(
              'Success',
              editApiLengthMs,
              undefined,
              stepName
            );

            await handleListPrice();
            history.push(paths.workOrders, {
              workOrder: editedWorkOrder
            });
            workOrderNotification('EDIT_SUCCESS');
          } catch (editError) {
            const editApiLengthMs = Math.round(
              performance.now() - editStartedAt
            );
            workOrderEvents.sendSaveEditsEvent(
              'Error',
              editApiLengthMs,
              editError,
              stepName
            );

            throw editError;
          }
          return;
        }

        createWorkOrderPayload = values;
        const createWorkOrderStartedAt = performance.now();

        const createdWorkOrder = await createWorkOrder(values)
          .then(response => {
            createWorkOrderApiLengthMs = Math.round(
              performance.now() - createWorkOrderStartedAt
            );
            createWorkOrderCompleted = true;
            return response;
          })
          .catch(error => {
            createWorkOrderApiLengthMs = Math.round(
              performance.now() - createWorkOrderStartedAt
            );
            createWorkOrderCompleted = true;
            throw error;
          });

        await handleListPrice();

        history.push(paths.workOrders);

        workOrderNotification('CREATE_SUCCESS', undefined, {
          onDuplicate: () => {
            workOrderEvents.sendInitiateDuplicateEvent('Confirmation toast');

            history.push(
              `${paths.workOrders}/create`,
              createDuplicateLocationState(
                createdWorkOrder,
                OperationTypeEnum.WORK_ORDER
              )
            );
          },
          onPrint: () => {
            const event = new CustomEvent('printWorkOrder', {
              detail: { workOrder: createdWorkOrder }
            });
            globalThis.dispatchEvent(event);
          }
        });

        // send save work order success event to Amplitude with API duration and payload details
        workOrderEvents.sendSaveEvent(
          'Success',
          createWorkOrderApiLengthMs,
          createWorkOrderPayload,
          isDuplicateWorkOrderFlow
        );
      } catch (e) {
        datadogLogs.logger.error('Error saving work order', { error: e });
        workOrderNotification('CREATE_OR_EDIT_ERROR');

        if (
          !utilityState.isEditMode &&
          createWorkOrderCompleted &&
          createWorkOrderPayload
        ) {
          workOrderEvents.sendSaveEvent(
            'Error',
            createWorkOrderApiLengthMs,
            createWorkOrderPayload,
            isDuplicateWorkOrderFlow,
            e
          );
        }
      } finally {
        setIsSavingForm(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      history,
      dispatch,
      saveTankInfoPreferences,
      createWorkOrder,
      editWorkOrder,
      values,
      utilityState.isEditMode,
      handleListPrice,
      workOrderEvents,
      isDuplicateWorkOrderFlow
    ]
  );

  // Products utility helper - flexible for future development
  const updateProductUtilities = useCallback(
    (updates: Record<string, any>) => {
      updateUtilityState({
        products: { ...utilityState.products, ...updates }
      });
    },
    [updateUtilityState, utilityState.products]
  );

  // Helper to check if application dates are in the future (weather not available)
  const areDatesInFuture = useCallback((): boolean => {
    const startDate = values?.startDateTime;
    const endDate = values?.endDateTime;
    // Use UTC time for comparison to ensure consistent behavior across time zones
    const nowUtc = Date.now();
    const isStartDateInFuture = startDate && startDate.valueOf() > nowUtc;
    const isEndDateInFuture = endDate && endDate.valueOf() > nowUtc;
    return Boolean(isStartDateInFuture || isEndDateInFuture);
  }, [values?.startDateTime, values?.endDateTime]);

  const contextValue: AgOperationUtilityContextType = useMemo(
    () => ({
      utilityState,
      updateUtilityState,
      setAutoWeatherEnabled,
      setHasModifiedWeatherData,
      fetchWeatherData,
      resetOriginalWeatherData,
      isWeatherLoading: loading,
      weatherError: weatherError ?? null,
      apiErrorValue: apiErrorValue ?? null,
      originalWeatherData,
      isSavingForm,
      handleSaveApplication,
      handleSaveWorkOrder,
      updateProductUtilities,
      operationType,
      setOperationType,
      areDatesInFuture,
      hasVisitedWeatherTab,
      setHasVisitedWeatherTab,
      setOriginalDates,
      setOriginalCropZones,
      propagateApplicationMethodToProducts
    }),
    [
      utilityState,
      updateUtilityState,
      setAutoWeatherEnabled,
      setHasModifiedWeatherData,
      fetchWeatherData,
      resetOriginalWeatherData,
      loading,
      weatherError,
      apiErrorValue,
      originalWeatherData,
      isSavingForm,
      handleSaveApplication,
      handleSaveWorkOrder,
      updateProductUtilities,
      operationType,
      setOperationType,
      areDatesInFuture,
      hasVisitedWeatherTab,
      setOriginalDates,
      setOriginalCropZones,
      propagateApplicationMethodToProducts
    ]
  );

  return (
    <AgOperationUtilityContext.Provider value={contextValue}>
      {children}
    </AgOperationUtilityContext.Provider>
  );
};

/**
 * useAgOperationUtility - Hook to access Agricultural Operation utility state
 *
 * Available across all agricultural operation flows (create, edit, work orders, etc.)
 *
 * Usage Examples:
 *
 * // Weather utilities (implemented)
 * const { utilityState, setAutoWeatherEnabled, fetchWeatherData } = useAgOperationUtility();
 * const isWeatherAutoEnabled = utilityState.autoWeatherEnabled;
 *
 * // Auto-fetch weather when saving application (if toggle is enabled)
 * const { handleSaveApplication } = useAgOperationUtility();
 * const onSaveApplication = async () => {
 *   await handleSaveApplication(); // Automatically fetches weather if toggle enabled and data missing
 *   // Continue with save logic
 *   submitForm();
 * };
 *
 * // Manual weather fetch (WeatherStep usage)
 * const handleRefresh = async () => {
 *   await fetchWeatherData(); // Always fetches weather data
 * };
 *
 * // Products utilities (flexible for future development)
 * const { updateProductUtilities } = useAgOperationUtility();
 * const productSettings = utilityState.products;
 *
 * // Example future usage:
 * updateProductUtilities({ autoCalculationEnabled: true });
 * updateProductUtilities({ calculationMode: 'advanced', autoValidation: false });
 */
export const useAgOperationUtility = (): AgOperationUtilityContextType => {
  const context = useContext(AgOperationUtilityContext);
  if (!context) {
    throw new Error(
      'useAgOperationUtility must be used within an AgOperationUtilityProvider'
    );
  }
  return context;
};
